// src/pages/markets/board/api/useGroupStats.ts
//
// Only mounted behind an open panel: the query is enabled by the caller, so a
// closed panel costs nothing. The key carries the whole filter signature even
// though only part of it reaches the wire — over-keying splits a cache entry,
// under-keying serves the wrong column's numbers.

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchGroupStats } from './boardApi';
import { boardKeys, filterSignature } from './boardKeys';
import type { BoardFilters, BoardGroupStatsResponse } from './boardTypes';

const STATS_STALE_MS = 60_000;
const STATS_GC_MS = 10 * 60_000;

export interface UseGroupStatsOptions {
    group: string | null;
    filters: BoardFilters;
    window: string;
    bucketLimit?: number;
    topLimit?: number;
    enabled?: boolean;
}

export interface GroupStatsResult {
    data: BoardGroupStatsResponse | null;
    isLoading: boolean;
    isFetching: boolean;
    error: Error | null;
}

export function useGroupStats(opts: UseGroupStatsOptions): GroupStatsResult {
    const { group, filters, window, bucketLimit, topLimit, enabled = true } = opts;
    const active = enabled && Boolean(group);
    const signature = filterSignature(filters);

    const query = useQuery<BoardGroupStatsResponse, Error>({
        queryKey: boardKeys.groupStats(group ?? '', signature, window),
        queryFn: ({ signal }) =>
            fetchGroupStats({
                group: group as string,
                window,
                eventsInclude: filters.eventsInclude,
                eventsExclude: filters.eventsExclude,
                tagsInclude: filters.tagsInclude,
                tagsExclude: filters.tagsExclude,
                tagMatch: filters.tagMatch,
                minCapital: filters.minCapital,
                minTraders: filters.minTraders,
                maxAgeDays: filters.maxAgeDays,
                bucketLimit,
                topLimit,
                signal,
            }),
        enabled: active,
        staleTime: STATS_STALE_MS,
        gcTime: STATS_GC_MS,
        placeholderData: keepPreviousData,
    });

    return {
        data: query.data ?? null,
        isLoading: active && query.isLoading,
        isFetching: query.isFetching,
        error: (query.error as Error) ?? null,
    };
}